import { filter, forEach, pipe } from 'remeda'

type CarouselElement = HTMLElement & {
  readonly goToSlide: (index: number, behavior?: ScrollBehavior) => void
}

type GalleryHookContext = {
  readonly el: HTMLElement
  _galleryCleanup?: () => void
}

const isCarouselElement = (element: Element): element is CarouselElement =>
  element instanceof HTMLElement &&
  element.tagName === 'WA-CAROUSEL' &&
  'goToSlide' in element &&
  typeof element.goToSlide === 'function'

const isHTMLElement = (element: Element | null): element is HTMLElement =>
  element instanceof HTMLElement

const carouselViewport = (carousel: HTMLElement): HTMLElement | undefined => {
  const viewport =
    carousel.shadowRoot?.querySelector('[part~="scroll-container"], .slides') ??
    null

  return isHTMLElement(viewport) ? viewport : undefined
}

const currentSlideIndex = (
  viewport: HTMLElement | undefined,
  slideCount: number
): number => {
  if (
    slideCount === 0 ||
    viewport === undefined ||
    viewport.clientWidth === 0
  ) {
    return 0
  }

  return Math.min(
    slideCount - 1,
    Math.max(0, Math.round(viewport.scrollLeft / viewport.clientWidth))
  )
}

const showSlide = (
  carousel: HTMLElement,
  index: number,
  behavior: ScrollBehavior
): void => {
  if (isCarouselElement(carousel)) {
    carousel.goToSlide(index, behavior)
    return
  }

  const viewport = carouselViewport(carousel)

  viewport?.scrollTo({
    behavior,
    left: index * viewport.clientWidth
  })
}

const isThumbnailButton = (
  element: Element | null
): element is HTMLButtonElement =>
  element instanceof HTMLButtonElement &&
  element.hasAttribute('data-gallery-thumbnail')

const thumbnailButtons = (gallery: HTMLElement): readonly HTMLButtonElement[] =>
  pipe(
    Array.from(gallery.querySelectorAll('[data-gallery-thumbnail]')),
    filter(isThumbnailButton)
  )

const parseSlideIndex = (
  value: string | null,
  slideCount: number
): number | undefined => {
  if (value === null) {
    return undefined
  }

  const index = Number.parseInt(value, 10)

  return Number.isInteger(index) && index >= 0 && index < slideCount
    ? index
    : undefined
}

const slideIndexFromEvent = (event: Event): number | undefined => {
  if (!(event instanceof CustomEvent)) {
    return undefined
  }

  const detail: unknown = event.detail

  return typeof detail === 'object' &&
    detail !== null &&
    'index' in detail &&
    typeof detail.index === 'number'
    ? detail.index
    : undefined
}

const syncThumbnails = (
  buttons: readonly HTMLButtonElement[],
  activeIndex: number,
  scroll: boolean
): void => {
  pipe(
    buttons,
    forEach((button, index) => {
      const active = index === activeIndex
      button.setAttribute('aria-current', String(active))

      if (active && scroll) {
        button.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest'
        })
      }
    })
  )
}

const setupGallery = (gallery: HTMLElement): (() => void) => {
  const carousel = gallery.querySelector('[data-part="gallery-carousel"]')

  if (!(carousel instanceof HTMLElement)) {
    return () => undefined
  }

  const controller = new AbortController()
  const buttons = thumbnailButtons(gallery)

  const handleThumbnailClick = (event: Event): void => {
    const target = event.target
    const button =
      target instanceof Element
        ? target.closest('[data-gallery-thumbnail]')
        : null

    if (!(isThumbnailButton(button) && gallery.contains(button))) {
      return
    }

    const index = parseSlideIndex(
      button.getAttribute('data-gallery-thumbnail'),
      buttons.length
    )

    if (index === undefined) {
      return
    }

    syncThumbnails(buttons, index, true)
    showSlide(carousel, index, 'smooth')
  }

  const handleSlideChange = (event: Event): void => {
    const index = slideIndexFromEvent(event)

    if (index !== undefined) {
      syncThumbnails(buttons, index, true)
    }
  }

  gallery.addEventListener('click', handleThumbnailClick, {
    signal: controller.signal
  })
  carousel.addEventListener('wa-slide-change', handleSlideChange, {
    signal: controller.signal
  })

  customElements.whenDefined('wa-carousel').then(() => {
    if (controller.signal.aborted) {
      return
    }

    const viewport = carouselViewport(carousel)

    syncThumbnails(buttons, currentSlideIndex(viewport, buttons.length), false)

    viewport?.addEventListener(
      'scroll',
      () =>
        syncThumbnails(
          buttons,
          currentSlideIndex(viewport, buttons.length),
          false
        ),
      { signal: controller.signal }
    )
  })

  return () => controller.abort()
}

export const FacadeGallery = {
  mounted(this: GalleryHookContext) {
    this._galleryCleanup = setupGallery(this.el)
  },

  updated(this: GalleryHookContext) {
    this._galleryCleanup?.()
    this._galleryCleanup = setupGallery(this.el)
  },

  destroyed(this: GalleryHookContext) {
    this._galleryCleanup?.()
    this._galleryCleanup = undefined
  }
}
